import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { CarouselModule, Carousel } from 'primeng/carousel';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ApiService } from '../services/api.service';
import { Router, RouterLink } from '@angular/router';
import { UtilService } from '../services/util.service';
import { RecentViewComponent } from '../shared/recent-view/recent-view.component';
import { Subscription } from 'rxjs';
// import { TooltipModule } from 'primeng/tooltip';
import { DatePipe, isPlatformBrowser, NgIf, NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TstExpoBannerComponent } from '../shared/tst-expo-banner/tst-expo-banner.component';
// import { ShippingFeesModalComponent } from '../shared/shipping-fees-modal/shipping-fees-modal.component';
import { ProductCardComponent } from '../shared/product-card/product-card.component';
import { FeaturedCarouselComponent } from '../shared/featured-carousel/featured-carousel.component';
import { HomeSkeletonComponent } from '../shared/home-skeleton/home-skeleton.component';
import { SkeletonState } from '../shared/skeleton/skeleton-state';
import { CutoffCardComponent } from '../shared/cutoff-card/cutoff-card.component';
import { HomeWhyComponent } from '../shared/home-why/home-why.component';
import { HomeAskComponent } from '../shared/home-ask/home-ask.component';

/** Homepage sections, in page order — the cascade reveals them in this sequence. */
const SK_SECTIONS = [
  'hero', 'hiw', 'categories', 'featured', 'modify', 'brands',
  'testimonials', 'recent', 'companies', 'numbers', 'story', 'insights', 'faq',
] as const;
type SkSection = typeof SK_SECTIONS[number];

/** Longest the homepage will shimmer before revealing regardless. A failed or
 *  very slow API must never leave the page shimmering indefinitely. */
const SK_MAX_WAIT_MS = 6000;

/** Sections whose content is on screen before any API answers — pure static markup,
 *  or (for `categories`) real data seeded into the component. The server renders all
 *  of these in full, so on a reload the user is already looking at the finished
 *  section; shimmering it would replace good content with a placeholder, which is the
 *  "blink on reload" the client reported. They never enter the skeleton at all.
 *  `recent` is here too: app-recent-view owns its own data and hides itself when
 *  there's nothing to show, so the homepage has no load to gate a skeleton on. */
const SK_PAINTED_SECTIONS: readonly SkSection[] = [
  'hero', 'hiw', 'modify', 'numbers', 'categories', 'recent',
];

@Component({
  selector: 'app-categories',
  imports: [
    CarouselModule,
    ButtonModule,
    TagModule,
    RecentViewComponent,
    // TooltipModule,
    NgIf,
    FormsModule,
    RouterLink,
    TstExpoBannerComponent,
    // ShippingFeesModalComponent,
    ProductCardComponent,
    FeaturedCarouselComponent,
    CutoffCardComponent,
    HomeWhyComponent,
    HomeAskComponent,
    NgOptimizedImage,
    DatePipe,
    HomeSkeletonComponent
  ],
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.css',
})
export class CategoriesComponent implements OnInit, AfterViewInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private zone = inject(NgZone);
  @ViewChild('featuredCarousel') featuredCarousel!: Carousel;
  debounceTimer: any;
  feesCart: any;

  // "02 Six industries" cards (Wireframe_v2). Links go to the matching top-level category;
  // awning and marine share the awning-marine category until marine gets its own.
  // `img` is the base name in /images (.png, plus .webp unless `webp: false`).
  industries: { no: string; title: string[]; sub: string; img: string; webp?: boolean; cta: string; link: string }[] = [
    { no: '01', title: ['Awning', '& shading'], sub: 'Fabric, frames, hardware', img: 'first', cta: 'Shop awning', link: '/category/awning-marine' },
    { no: '02', title: ['Signage', '& display'], sub: 'Vinyl, substrates, LEDs', img: 'second', cta: 'Shop signage', link: '/category/sign-display' },
    { no: '03', title: ['Printing', '& inks'], sub: 'Printers, inks, media', img: 'third', cta: 'Shop printing', link: '/category/print-inks' },
    { no: '04', title: ['Marine', '& boating'], sub: 'Canvas, vinyl, fittings', img: 'fourth', cta: 'Shop marine', link: '/category/awning-marine' },
    { no: '05', title: ['Lighting', '& electrical'], sub: 'LED modules, power, neon', img: 'fifth', webp: false, cta: 'Shop lighting', link: '/category/lighting-electrical' },
    { no: '06', title: ['Equipment', '& tools'], sub: 'Printers, cutters, laminators', img: 'sixth', webp: false, cta: 'Shop equipment', link: '/category/equipment' },
  ];

  /**
   * Old homepage sections that are not in Wireframe_v2 (Modify, Brands marquee,
   * Testimonials, Recently bought, Companies, Numbers, Story, Insights, FAQ). Switched off,
   * not deleted, until the new homepage is finished; then delete them and this flag.
   */
  readonly showLegacySections = false;

  /** "08 Client companies": Figma's 8 x 2 grid of empty cells, shown until the logos load. */
  readonly clientPlaceholders = Array.from({ length: 16 }, (_, i) => i);
  /** Set once /website-sections answers; an empty partner list then drops the section. */
  companiesLoaded = false;
  // "05 Popular by demand" grid (Wireframe_v2). Figma names, linked to the closest real
  // subcategory; images are those subcategories' own. The API has no SKU counts, so `skus`
  // stays null (and hidden) rather than showing the Figma's placeholder numbers.
  popularCategories: { no: string; title: string; link: string; img: string; skus: string | null }[] = [
    { no: '01', title: 'Cast & calendered vinyl', link: '/category/sign-display/sign-vinyl', img: '513000.jpg', skus: null },
    { no: '02', title: 'Rigid substrates', link: '/category/sign-display/substrates', img: '510000.jpg', skus: null },
    { no: '03', title: 'Laminates & overlams', link: '/category/print-inks/overlaminates-mounting-adhesive', img: '403000.jpg', skus: null },
    { no: '04', title: 'Banner media', link: '/category/sign-display/banner-accessories', img: '501000.jpg', skus: null },
    { no: '05', title: 'Inks & cleaners', link: '/category/print-inks/inks', img: '402000.JPG', skus: null },
    { no: '06', title: 'Awning fabric', link: '/category/awning-marine/awning-marine-fabrics', img: '203000.jpg', skus: null },
    { no: '07', title: 'LED & electrical', link: '/category/lighting-electrical/led-lighting-electrical', img: '504000.jpg', skus: null },
    { no: '08', title: 'Hardware & fasteners', link: '/category/sign-display/sign-mounting-hardware', img: '502000.jpg', skus: null },
  ];
  readonly categoryImageBase = 'https://sfsuppleis.blob.core.windows.net/images/categories/';

  @ViewChild('brandRow') private brandRowRef?: ElementRef<HTMLElement>;
  brandCanPrev = false;
  brandCanNext = false;

  /** Prev/next move the row by one card (card width + the 20px gap). */
  scrollBrands(dir: 1 | -1): void {
    const row = this.brandRowRef?.nativeElement;
    const card = row?.firstElementChild as HTMLElement | null;
    if (!row || !card) return;
    row.scrollBy({ left: dir * (card.offsetWidth + 20), behavior: 'smooth' });
  }

  /** Arrows are only live when the row overflows (6 logos fit on desktop). */
  updateBrandArrows(): void {
    const row = this.brandRowRef?.nativeElement;
    if (!row) return;
    this.brandCanPrev = row.scrollLeft > 2;
    this.brandCanNext = row.scrollLeft + row.clientWidth < row.scrollWidth - 2;
  }

  // Hero value-prop carousel: auto-rotates every 6s (pauses on hover), pills sync.
  heroCards = [
    { icon: 'stock.svg', title: 'In-Stock Inventory', text: 'Thousands of products ready for same-day shipment and project fulfillment.', status: 'Thousands of products in stock' },
    { icon: 'custom.svg', title: 'Custom Services', text: 'Material processed to your specifications before delivery.', status: 'Precision processing available' },
    { icon: 'pricing.svg', title: 'Commercial Pricing', text: 'Account-based pricing built specifically for contractors and industrial buyers.', status: 'Contractor pricing available' },
    { icon: 'free-delivery.svg', title: 'Fast Delivery', text: 'Reliable fulfillment and delivery options to keep projects moving.', status: 'Same-day processing available' },
  ];
  activeHeroCard = 0;
  // Hero base background is white on first load, then flips dark (#0A0A0A) once the
  // first card slides away — one-way, stays dark for the rest of the session.
  heroCardMoved = false;
  private heroRotationTimer: any = null;

  // "Explore categories" grid — image + title + subtitle. Using existing product
  // images as placeholders (swap for real category art later). Links to listing.
  // Curated subtitles (a few representative subcategories) keyed by category slug —
  // the catalog API doesn't return a subtitle, so we map it here.
  private readonly categorySubtitles: Record<string, string> = {
    'sign-display': 'Substrates, sign vinyl, banners',
    'print-inks': 'Digital media, inks, overlaminates',
    'lighting-electrical': 'LED lighting, neon',
    'awning-marine': 'Tubing & extrusions, fabrics, hardware',
    'equipment': 'Wide-format equipment',
  };
  // Fallback for categories the API returns without an image (e.g. Lighting & Electrical) —
  // same placeholder the category-list page uses (category-list.component.html).
  private readonly categoryFallbackImage = 'placeholder.svg';

  // Explore-Categories cards. SEEDED with the real top-level categories (names, blob
  // images, slugs from GET /categories/all/children) so SSR / no-JS / API-failure all
  // render correct content; loadCategories() refreshes them (first 6) live in the browser.
  categories: { image: string; title: string; subtitle: string; link: string }[] = [
    { image: 'https://sfsuppleis.blob.core.windows.net/images/categories/500000.jpg', title: 'Sign & Display', subtitle: this.categorySubtitles['sign-display'], link: '/category/sign-display' },
    { image: 'https://sfsuppleis.blob.core.windows.net/images/categories/400000.jpg', title: 'Print & Inks', subtitle: this.categorySubtitles['print-inks'], link: '/category/print-inks' },
    { image: this.categoryFallbackImage, title: 'Lighting & Electrical', subtitle: this.categorySubtitles['lighting-electrical'], link: '/category/lighting-electrical' },
    { image: 'https://sfsuppleis.blob.core.windows.net/images/categories/200000.jpg', title: 'Awning & Marine', subtitle: this.categorySubtitles['awning-marine'], link: '/category/awning-marine' },
    { image: 'https://sfsuppleis.blob.core.windows.net/images/categories/600000.jpg', title: 'Equipment', subtitle: this.categorySubtitles['equipment'], link: '/category/equipment' },
  ];

  // "Modify Your Materials" (S&F services) cards — icon + title + hover-revealed description.
  modifyServices = [
    { icon: '/images/svg/services/cutting.svg', title: 'Cutting', desc: 'Cut the product to your custom specs, create various combos.' },
    { icon: '/images/svg/services/bending.svg', title: 'Bending', desc: 'Bend material to precise shapes and angles on demand.' },
    { icon: '/images/svg/services/slitting.svg', title: 'Slitting', desc: 'Slit material into exact strips and custom widths.' },
    { icon: '/images/svg/services/punching.svg', title: 'Punching', desc: 'Add clean, standard edge punching to your material.' },
  ];

  // "How It Works" steps + scroll-triggered sequence animation (staggered via CSS delays).
  hiwSteps = ['Choose Materials', 'Add Custom Services', 'Same-Day Processing', 'Delivered Fast'];
  hiwReady = false;   // true only in the browser with motion allowed (drives the hidden->animate states)
  hiwInView = false;  // set by the IntersectionObserver when the section scrolls into view
  @ViewChild('hiwSection') private hiwSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('heroCardFade') private heroCardFadeRef?: ElementRef<HTMLElement>;

  // "S&F in Numbers" — count-up stats that animate once when ~40% of the section
  // scrolls into view. Left-to-right stagger (120ms/card). `numbersDisplay` seeds
  // the FINAL values (SSR / reduced-motion show real numbers); the count resets
  // them to 0 only when JS + motion are available.
  numbersStats = [
    { value: 50, suffix: '', label: 'States', text: 'Reliable shipping across the U.S.', bg: '/images/numbers/bg-states.svg' },
    { value: 40, suffix: '+', label: 'Years', text: 'Serving commercial contractors since 1985.', bg: '/images/numbers/bg-years.svg' },
    { value: 1000, suffix: '+', label: 'Materials', text: 'Industrial-grade inventory ready to ship.', bg: '/images/numbers/bg-materials.svg' },
    { value: 20, suffix: '+', label: 'Brands', text: 'Trusted professional-grade manufacturers.', bg: '/images/numbers/bg-brands.svg' },
  ];
  numbersDisplay: number[] = this.numbersStats.map(s => s.value);
  numbersJs = false;     // true once JS + motion take over (drives the hidden→reveal CSS)
  numbersInView = false; // true once the section is ~40% visible
  @ViewChild('numbersSection') private numbersSectionRef?: ElementRef<HTMLElement>;

  /** Runs OUTSIDE the Angular zone on purpose. zone.js counts a periodic timer as a task
   *  that never finishes, so ApplicationRef.isStable() never emits true, hydration never
   *  completes (NG0506) and Angular eventually discards the server-rendered DOM and
   *  re-renders the whole app — which looks like the page loading a second time, with a
   *  blink in between. Re-entering the zone for the tick keeps the UI updating normally. */
  startHeroRotation(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.stopHeroRotation();
    this.zone.runOutsideAngular(() => {
      this.heroRotationTimer = setInterval(() => {
        this.zone.run(() => {
          this.activeHeroCard = (this.activeHeroCard + 1) % this.heroCards.length;
          this.heroCardMoved = true; // first auto-advance darkens the hero
          this.replayCardAnimation();
        });
      }, 6000);
    });
  }

  stopHeroRotation(): void {
    if (this.heroRotationTimer) {
      clearInterval(this.heroRotationTimer);
      this.heroRotationTimer = null;
    }
  }

  setHeroCard(index: number): void {
    this.activeHeroCard = index;
    if (index !== 0) this.heroCardMoved = true; // picking any later card darkens the hero
    this.replayCardAnimation();
    this.startHeroRotation(); // restart the 6s timer after a manual pick
  }

  /** Replay the 250ms fade+slide on the card body. Angular animations are noop-provided
   *  and the @for re-mount didn't reliably restart the CSS animation, so we restart it
   *  explicitly via the classic remove-class -> force-reflow -> add-class trick. */
  private replayCardAnimation(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = this.heroCardFadeRef?.nativeElement;
    if (!el) return;
    el.classList.remove('animate-in');
    void el.offsetWidth; // force reflow so the animation can start over
    el.classList.add('animate-in');
  }

  product = [
    {
      img: 'product-1.png',
      btn: 'Options',
    },
    {
      img: 'product-2.png',
      btn: '+Add',
    },
    {
      img: 'product-3.png',
      btn: 'Options',
    },
    {
      img: 'product-1.png',
      btn: '+Add',
    },
    {
      img: 'product-1.png',
      btn: 'Options',
    },
  ];
  // header = inject(HeaderComponent);
  brands: any = [];
  // Capped subset rendered in the "Brands we carry" marquee (duplicated in the
  // template for a seamless loop). Full list still powers the /brands page.
  marqueeBrands: any = [];

  // "What contractors are saying" testimonials — customerFeedbacks from /website-sections
  // (applyTestimonials); normalised to { quote, name, role, company, initials, image }.
  testimonials: any[] = [];
  testimonialSlides: any[][] = [];
  activeTestimonial = 0;
  private testimonialTimer: any = null;

  // "Companies we work with" logo wall — partnerCompanies from /website-sections
  // (applyCompanies); normalised to { name, src }.
  companyLogos: any[] = [];

  // "Our Story" — ourStories from /website-sections (applyStory); { tagline, header,
  // description, videoUrl }. null until it arrives, and stays null when nothing is
  // published, which removes the section rather than showing an empty frame.
  story: { tagline: string; header: string; description: string; videoUrl: string } | null = null;

  // "Insights & industry updates" — blogs from /website-sections (applyInsights,
  // browser-only). insightsPosts[0] = featured (left); the rest fill the scrollable
  // list (right).
  insightsPosts: any[] = [];
  insightsInView = false;
  insightsJs = false;
  // The bottom fade is a scroll hint, so it's only drawn when the list actually
  // overflows its fixed 668px height (measured — post count alone isn't reliable,
  // and below 992px the list is height:auto and never scrolls).
  insightsScrollable = false;
  @ViewChild('insightsSection') private insightsSectionRef?: ElementRef<HTMLElement>;
  @ViewChild('insightsList') private insightsListRef?: ElementRef<HTMLElement>;

  // FAQ accordion (single-open, first row open by default) — faqs from /website-sections
  // (applyFaqs), ordered by sortOrder, normalised to the { q, a } shape.
  faqs: any[] = [];
  faqOpen: number | null = 0;

  toggleFaq(i: number): void {
    this.faqOpen = this.faqOpen === i ? null : i;
  }
  cartList: any = [];
  user: any;
  qty: number = 0;
  isLogin: boolean = false;
  // showQuantityState: { [key: string]: boolean } = {};
  showEnabled: boolean = false;
  // addedToCart: boolean = false;
  // addedItem: any;
  showQuantityState: {
    [key: string]: {
      visible: boolean;
      locked?: boolean;
    };
  } = {};
  userSubscription = new Subscription();
  quotesSubscription = new Subscription();
  featuredProducts: any = [];
  canScrollFeatured = false;
  responsiveOptions = [
    { breakpoint: '1250px', numVisible: 4, numScroll: 3 },
    { breakpoint: '992px', numVisible: 3, numScroll: 2 },
    { breakpoint: '750px', numVisible: 2, numScroll: 1 },
  ];
  //  @ViewChild(PopUpComponent) popup!: PopUpComponent;
  // @ViewChild(ShippingFeesModalComponent)
  // popupShipFees!: ShippingFeesModalComponent;

  //  private addTocartSubject = new Subject<any>();
  constructor(
    private api: ApiService,
    private router: Router,
    private util: UtilService,
    private cdr: ChangeDetectorRef
  ) {
    // this.addTocartSubject.pipe(debounceTime(1000)).subscribe((cart) => {
    //       this.performAddToCart(cart);
    //     });
  }
  isMobileView: boolean = false;
  checkMobileView() {
    if (isPlatformBrowser(this.platformId)) {
      this.isMobileView = window.innerWidth <= 750;
      this.updateCanScrollFeatured();
      this.updateInsightsScrollable(); // the list is height:auto (never scrolls) under 992px
      // Regroup the testimonial carousel when a resize crosses the 768px line.
      const perSlide = this.testimonialPerSlide();
      if (this.testimonials.length && perSlide !== this.tmPerSlide) {
        this.buildTestimonialSlides(perSlide);
        this.startTestimonialAutoplay();
      }
    }
  }

  updateCanScrollFeatured() {
    if (!isPlatformBrowser(this.platformId) || !this.featuredProducts?.length) {
      this.canScrollFeatured = false;
      return;
    }
    const width = window.innerWidth;
    let numVisible = 4;
    for (const opt of this.responsiveOptions) {
      if (width <= parseInt(opt.breakpoint, 10)) {
        numVisible = opt.numVisible;
      }
    }
    this.canScrollFeatured = this.featuredProducts.length > numVisible;
  }

  /** Featured Products auto-scroll pause/resume on hover (Figma carousel spec).
   *  Guarded so it's a no-op if PrimeNG's autoplay API changes. */
  pauseFeaturedAutoplay() {
    (this.featuredCarousel as any)?.stopAutoplay?.();
  }

  resumeFeaturedAutoplay() {
    (this.featuredCarousel as any)?.startAutoplay?.();
  }

  /** Cards per testimonial slide currently in use — kept so a resize only rebuilds
   *  the slides when it actually crosses the phone/desktop boundary. */
  private tmPerSlide = 3;

  /** Figma mobile shows ONE testimonial per slide (with the next one peeking);
   *  768px and up keeps the 3-up row. */
  private testimonialPerSlide(): number {
    return isPlatformBrowser(this.platformId) && window.innerWidth <= 767.98 ? 1 : 3;
  }

  /** Group testimonials into slides of `perSlide` cards (3 on desktop, 1 on phones). */
  buildTestimonialSlides(perSlide = this.testimonialPerSlide()): void {
    this.tmPerSlide = perSlide;
    this.testimonialSlides = [];
    for (let i = 0; i < this.testimonials.length; i += perSlide) {
      this.testimonialSlides.push(this.testimonials.slice(i, i + perSlide));
    }
    if (this.activeTestimonial >= this.testimonialSlides.length) {
      this.activeTestimonial = 0;
    }
  }

  /** Auto-advance the testimonials carousel every 6s (browser only). Outside the Angular
   *  zone for the same reason as the hero rotation — see startHeroRotation. */
  startTestimonialAutoplay(): void {
    if (!this.showLegacySections) return;
    if (!isPlatformBrowser(this.platformId) || this.testimonialSlides.length <= 1) return;
    this.stopTestimonialAutoplay();
    this.zone.runOutsideAngular(() => {
      this.testimonialTimer = setInterval(() => {
        this.zone.run(() => {
          this.activeTestimonial = (this.activeTestimonial + 1) % this.testimonialSlides.length;
        });
      }, 6000);
    });
  }

  stopTestimonialAutoplay(): void {
    if (this.testimonialTimer) {
      clearInterval(this.testimonialTimer);
      this.testimonialTimer = null;
    }
  }

  /** Dot navigation: jump to a slide and restart the 6s timer. */
  goToTestimonial(index: number): void {
    this.activeTestimonial = index;
    this.startTestimonialAutoplay();
  }

  /** Refresh the Explore-Categories cards from the live catalog API (browser only).
   *  Maps the first 6 top-level categories to cards (real name, blob image, slug) with a
   *  curated subtitle. Shows fewer than 6 if the catalog returns fewer. Any failure keeps
   *  the seeded fallback so the section is never empty. */
  private async loadCategories(): Promise<void> {
    try {
      const data = await this.api.getChildCategories('all');
      if (Array.isArray(data) && data.length) {
        this.categories = data.slice(0, 6).map((c: any) => ({
          image: c.imageURL || this.categoryFallbackImage,
          title: c.categoryName,
          subtitle: this.categorySubtitles[c.urlSlug] || '',
          link: '/category/' + c.urlSlug,
        }));
      }
    } catch {
      // keep the seeded fallback
    }
  }

  private avatarInitials(name: string): string {
    return (name || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  }

  private static readonly EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

  /** /website-sections is a display-only DTO: the backend deliberately leaves the row
   *  ids unset, so every row arrives as the empty GUID. That can NOT be a @for track key
   *  — identical keys make Angular throw NG0955 and the loop stops rendering — so rows
   *  are keyed by position instead. Confirmed intentional, not a bug to wait on; the
   *  check still prefers a real id if one is ever sent. */
  private sectionId(raw: any, prefix: string, i: number): string {
    return raw && raw !== CategoriesComponent.EMPTY_GUID ? String(raw) : `${prefix}-${i}`;
  }

  private static bySortOrder(a: any, b: any): number {
    return (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
  }

  /** Every CMS-driven homepage section in ONE request. Replaces the five separate
   *  calls this used to make (faq / our-story / customer-feedback / partner-company /
   *  blog list). Each section is mapped to the exact shape its template already uses,
   *  and a failure leaves them empty (or null for the story) exactly as before, so a
   *  dead endpoint degrades to "section hidden" rather than a broken page.
   *
   *  NOTE: every row currently comes back with isPublished:false, so nothing is filtered
   *  on that flag — the endpoint is specified to return published rows only. */
  private async loadWebsiteSections(): Promise<void> {
    let data: any;
    try {
      data = await this.api.getWebsiteSections();
    } catch {
      this.companiesLoaded = true;
      return; // sections stay empty; revealOn still fires so no skeleton is left spinning
    }
    this.companiesLoaded = true;
    this.applyFaqs(data?.faqs);
    this.applyStory(data?.ourStories);
    this.applyTestimonials(data?.customerFeedbacks);
    this.applyCompanies(data?.partnerCompanies);
    this.applyInsights(data?.blogs);
  }

  /** FAQ accordion, ordered by sortOrder, normalised to the { q, a } shape. */
  private applyFaqs(rows: any): void {
    const list = Array.isArray(rows) ? rows : [];
    this.faqs = list
      .slice()
      .sort(CategoriesComponent.bySortOrder)
      .map((x: any, i: number) => ({
        id: this.sectionId(x.frequentlyAskedQuestionId, 'faq', i),
        q: x.question,
        a: x.answer,
      }));
    this.faqOpen = this.faqs.length ? 0 : null;
  }

  /** "Our Story" is a list in the CMS but a single block in the design — lowest
   *  sortOrder wins. Left null when nothing comes back, which drops the section. */
  private applyStory(rows: any): void {
    const first = (Array.isArray(rows) ? rows : []).slice().sort(CategoriesComponent.bySortOrder)[0];
    if (!first?.header) return;
    this.story = {
      tagline: first.tagline || '',
      header: first.header,
      description: first.description || '',
      videoUrl: first.videoUrl || first.videoURL || first.video || '',
    };
  }

  /** Testimonial carousel. Cards fall back to the initials badge when imageUrl is empty. */
  private applyTestimonials(rows: any): void {
    const list = Array.isArray(rows) ? rows : [];
    this.testimonials = list
      .slice()
      .sort(CategoriesComponent.bySortOrder)
      .map((x: any, i: number) => ({
        id: this.sectionId(x.customerFeedbackId, 'feedback', i),
        quote: x.message,
        name: x.customerName,
        role: x.title || '',
        company: x.companyName || '',
        initials: this.avatarInitials(x.customerName),
        image: x.imageUrl || '',
      }));
    this.buildTestimonialSlides();
    this.startTestimonialAutoplay();
  }

  /** "Companies we work with" logos → { name, src } for the existing mask template. */
  private applyCompanies(rows: any): void {
    const list = Array.isArray(rows) ? rows : [];
    this.companyLogos = list
      .slice()
      .sort(CategoriesComponent.bySortOrder)
      .map((x: any, i: number) => ({
        id: this.sectionId(x.partnerCompanyId, 'company', i),
        name: x.name,
        src: x.imageUrl,
      }));
  }

  /** "Insights & industry updates" — featured = insightsPosts[0], rest = scrollable list. */
  private applyInsights(rows: any): void {
    const list = Array.isArray(rows) ? rows : [];
    this.insightsPosts = list
      .slice()
      .sort(CategoriesComponent.bySortOrder)
      .map((x: any) => ({
        category: x.categoryName || '',
        categorySlug: x.categorySlug || '',
        title: x.title || '',
        excerpt: x.excerpt || '',
        date: x.publishDate || '',
        read: this.readingLabel(x),
        image: x.featuredImageUrl || '',
        link: ['/blog', x.slug],
      }));
    // The section is @if'd on insightsPosts.length, so it doesn't exist yet when
    // ngAfterViewInit runs — wire up the scroll reveal once it has rendered.
    if (this.insightsPosts.length) {
      setTimeout(() => {
        if (this.hiwReady) this.setupInsightsReveal();
        this.updateInsightsScrollable();
        // Lazy thumbs settle after this tick; re-measure once they're in.
        setTimeout(() => this.updateInsightsScrollable(), 600);
      }, 0);
    }
  }

  /** "X Min read" label from whatever the Blog API provides: an explicit reading-minutes
   *  field if present, otherwise derived from a word count (~200 wpm). Empty when neither
   *  is available (so the meta just shows the byline + date). */
  private readingLabel(x: any): string {
    const mins = x?.readingTimeMinutes ?? x?.readingMinutes ?? x?.readMinutes ?? x?.readingTime;
    if (mins != null && Number(mins) > 0) return `${Math.round(Number(mins))} Min read`;
    const words = x?.wordCount ?? x?.wordsCount;
    if (words != null && Number(words) > 0) return `${Math.max(1, Math.round(Number(words) / 200))} Min read`;
    return '';
  }

  // ── Progressive skeleton ──────────────────────────────────────────────────
  // A section shows its skeleton from the first painted frame until its own data
  // lands, then crossfades in — see SkeletonState.
  //  - SK_PAINTED_SECTIONS are resolved up front: static markup or seeded data, so
  //    they have nothing to wait for and render as content immediately.
  //  - Every other section reveals off its OWN load (revealOn), so the blog API
  //    being slow doesn't hold the FAQ (or anything else) behind a skeleton.
  private sk!: SkeletonState<SkSection>;

  skFadeIn(key: SkSection): boolean {
    return this.sk.fadeIn(key);
  }

  skShow(key: SkSection): boolean {
    return this.sk.show(key);
  }

  skReady(key: SkSection): boolean {
    return this.sk.ready(key);
  }

  skFading(key: SkSection): boolean {
    return this.sk.fading(key);
  }

  /** Reveal these sections as soon as THEIR OWN load settles — never gated on the
   *  rest of the page, so one slow endpoint can't shimmer sections that are ready.
   *  A rejected load still reveals (each loader keeps its seeded/empty fallback), and
   *  SK_MAX_WAIT_MS backstops a call that never returns at all. */
  private revealOn(keys: readonly SkSection[], load: Promise<any>): void {
    const settled = load.catch(() => undefined);
    // Server: nothing to reveal (every section is already content there) and no timer —
    // SK_MAX_WAIT_MS would be a pending macrotask that zone.js SSR waits on before it
    // serialises, i.e. 6s straight onto TTFB.
    if (!isPlatformBrowser(this.platformId)) return;
    const cap = new Promise((resolve) => setTimeout(resolve, SK_MAX_WAIT_MS));
    Promise.race([settled, cap]).then(() => this.sk.reveal(keys));
  }

  async ngOnInit() {
    this.sk = new SkeletonState(SK_SECTIONS, isPlatformBrowser(this.platformId), () =>
      this.cdr.detectChanges()
    );
    // Static / seeded / self-managing sections: on screen from the first frame.
    this.sk.resolve(SK_PAINTED_SECTIONS);

    this.checkMobileView();
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', this.checkMobileView.bind(this));
      // Brands and Featured are browser-only DELIBERATELY. They used to run during SSR,
      // and zone.js holds the response until every pending request finishes — those two
      // endpoints take ~1.7s and ~1.9s, which landed directly on TTFB (measured 1.8-2.7s
      // on the deployed site). The browser cannot paint anything at all until the
      // document arrives, so that wait WAS the white flash on reload; no amount of
      // skeleton work can cover it, because the skeleton is inside the HTML that hasn't
      // arrived yet. The cost of keeping them here is that the server has no data for
      // these two sections, so they serialise empty and only fill in once the browser's
      // own call lands (hydrated, they hold the server's empty markup for SK_IDLE_MS
      // and then shimmer). Moving them back into SSR would put them in the HTML for
      // crawlers, at ~1.7s and ~1.9s of TTFB — worth revisiting only behind a
      // server-side response cache.
      this.revealOn(['brands'], this.getBrands());
      this.revealOn(['featured'], this.getFeatured());
      // Browser-only. These five sections used to be five separate requests, each
      // revealing on its own; they now share ONE /website-sections call, so they all
      // reveal together the moment it lands.
      // The new "08 Client companies" section reads partnerCompanies from it, so it always
      // runs; the other four only render when the legacy sections are switched back on.
      this.revealOn(
        ['faq', 'testimonials', 'companies', 'insights', 'story'],
        this.loadWebsiteSections(),
      );
      // Refresh only — the Explore-Categories cards are already seeded and revealed,
      // so this swaps live data into a visible section instead of gating a skeleton.
      this.loadCategories();
    }

    this.userSubscription = this.util.user$.subscribe((user) => {
      this.user = user;
    });
    if (isPlatformBrowser(this.platformId)) {
      let token = localStorage.getItem('token');
      if (token) {
        this.isLogin = true;
      }
      // Only opt into the entrance animation when the user hasn't asked for reduced motion.
      this.hiwReady = !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    }

    this.startHeroRotation();

    this.buildTestimonialSlides();
    this.startTestimonialAutoplay();

    //  this.quotesSubscription = this.util.productDetailsFetch.subscribe((res)=>{
    //   if(res == true) this.getFeatured()
    // })
  }
  searchValue: string = '';
  onClickSearch() {
    if (!this.searchValue || this.searchValue.trim() === '') {
      this.searchValue = '';
    }
    console.log(this.searchValue, 'value ');
    // if(this.searchValue){

    this.util.triggerSearch(this.searchValue);
    // }
    this.router.navigate(['/search'], {
      queryParams: { q: this.searchValue.trim() },
    });
  }
  onchangeInput() {
    this.util.triggerSearchChange(this.searchValue);
  }
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) setTimeout(() => this.updateBrandArrows());
    if (!isPlatformBrowser(this.platformId) || !this.hiwReady) return;
    // Deferred one macrotask, NOT cosmetic: ngAfterViewInit runs INSIDE the change
    // detection cycle, and everything below sets a flag the template binds to
    // (numbersJs, hiwInView, insightsJs). Since the static sections are resolved up
    // front they now render in that very first pass, so writing those flags here is a
    // false→true change after the view was checked — dev mode throws NG0100 for it.
    // A timeout lands after the cycle closes, where the write is just a normal update.
    setTimeout(() => {
      this.setupNumbersReveal();
      this.setupInsightsReveal();
      if (!this.hiwSectionRef?.nativeElement) {
        this.hiwInView = true;
        return;
      }
      // Reveal the steps when the section scrolls into view. A window scroll listener is
      // used (it IS zone-patched, so change detection runs) rather than IntersectionObserver,
      // which is NOT zone-patched and left the .in-view class stuck off.
      window.addEventListener('scroll', this.hiwScrollHandler, { passive: true });
      this.checkHiwInView(); // reveal right away if already on screen
    }, 0);
  }

  private hiwScrollHandler = (): void => this.checkHiwInView();

  /** Reveals once, the first time the section scrolls into view, then stops listening. */
  private checkHiwInView(): void {
    if (this.hiwInView) return;
    const el = this.hiwSectionRef?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh * 0.85 && rect.bottom > 0) {
      this.hiwInView = true;
      window.removeEventListener('scroll', this.hiwScrollHandler);
    }
  }

  // ===== "S&F in Numbers" count-up reveal =====
  private numbersScrollHandler = (): void => this.checkNumbersInView();

  /** Browser + motion only (guarded by ngAfterViewInit). Switch to the JS-driven
   *  hidden→reveal state, reset counters to 0, and watch for the section scroll-in. */
  private setupNumbersReveal(): void {
    if (!this.numbersSectionRef?.nativeElement) return; // leave final values visible
    this.numbersJs = true;
    this.numbersDisplay = this.numbersStats.map(() => 0);
    window.addEventListener('scroll', this.numbersScrollHandler, { passive: true });
    setTimeout(() => this.checkNumbersInView(), 0); // fire immediately if already on screen
  }

  /** Counts up once, the first time ~40% of the section is in view, then stops listening. */
  private checkNumbersInView(): void {
    if (this.numbersInView) return;
    const el = this.numbersSectionRef?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const visible = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
    const ratio = rect.height > 0 ? visible / rect.height : 0;
    if (ratio >= 0.4 && rect.bottom > 0 && rect.top < vh) {
      this.numbersInView = true;
      window.removeEventListener('scroll', this.numbersScrollHandler);
      this.startNumberCounts();
    }
  }

  /** Left-to-right stagger: each card's count starts 120ms after the previous. */
  private startNumberCounts(): void {
    this.numbersStats.forEach((_, i) => setTimeout(() => this.animateCount(i), i * 120));
  }

  /** Count 0 → target over 1.2s with an ease-out curve (rAF is zone-patched, so CD runs). */
  private animateCount(i: number): void {
    const target = this.numbersStats[i].value;
    const duration = 1200;
    let startTs: number | null = null;
    const step = (ts: number): void => {
      if (startTs === null) startTs = ts;
      const t = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out
      this.numbersDisplay[i] = Math.round(target * eased);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        this.numbersDisplay[i] = target;
      }
    };
    requestAnimationFrame(step);
  }

  /** Thousands separators for the count (1000 → "1,000"). */
  formatStat(n: number): string {
    return n.toLocaleString('en-US');
  }

  // ===== "Insights & industry updates" =====
  /** Category → tag colour by content type: blue=educational/product, orange=
   *  actionable/help, green=customer success/spotlight, red=promo/sales, gray=news. */
  insightTagClass(name: string): string {
    const n = (name || '').toLowerCase();
    if (/how[\s-]?to|tip|help|tutorial|step/.test(n)) return 'tag-orange';
    if (/customer|success|spotlight|story|testimonial/.test(n)) return 'tag-green';
    if (/promo|sale|deal|discount|offer|clearance|save/.test(n)) return 'tag-red';
    if (/guide|product|material|learn|educat|brand/.test(n)) return 'tag-blue';
    if (/news|update|industry|announc|release/.test(n)) return 'tag-gray';
    return 'tag-gray';
  }

  /** Does the list overflow its fixed height? Drives the bottom fade hint. Re-run after
   *  render, after the thumbs load (they change the content height), and on resize. */
  updateInsightsScrollable(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = this.insightsListRef?.nativeElement;
    this.insightsScrollable = !!el && el.scrollHeight > el.clientHeight + 1;
  }

  private insightsScrollHandler = (): void => this.checkInsightsInView();

  /** Browser + motion only: switch to the hidden→reveal state and watch for the section
   *  to scroll into view (cards fade up, staggered). Called from both ngAfterViewInit and
   *  applyInsights (whichever sees the section first); the insightsJs flag makes the second
   *  call a no-op so the scroll listener is only bound once. */
  private setupInsightsReveal(): void {
    if (this.insightsJs || this.insightsInView) return;
    if (!this.insightsSectionRef?.nativeElement) return;
    this.insightsJs = true;
    window.addEventListener('scroll', this.insightsScrollHandler, { passive: true });
    setTimeout(() => this.checkInsightsInView(), 0);
  }

  private checkInsightsInView(): void {
    if (this.insightsInView) return;
    const el = this.insightsSectionRef?.nativeElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < vh * 0.85 && rect.bottom > 0) {
      this.insightsInView = true;
      window.removeEventListener('scroll', this.insightsScrollHandler);
    }
  }

  ngOnDestroy(): void {
    this.sk?.destroy();
    this.userSubscription.unsubscribe();
    this.quotesSubscription?.unsubscribe();
    this.stopHeroRotation();
    this.stopTestimonialAutoplay();
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('scroll', this.hiwScrollHandler);
      window.removeEventListener('scroll', this.numbersScrollHandler);
      window.removeEventListener('scroll', this.insightsScrollHandler);
    }
  }
  async getBrands() {
    let brands = await this.api.getBrands();
    this.brands = brands.filter((item: any) => !!item.imageUrl);
    this.marqueeBrands = this.brands.slice(0, 30);
    // The "03 Performance brands" row only knows whether it overflows once the cards render.
    setTimeout(() => this.updateBrandArrows());
  }
  private initQuantityState(slug: string) {
    if (!this.showQuantityState[slug]) {
      this.showQuantityState[slug] = { visible: false, locked: false };
    }
    return this.showQuantityState[slug];
  }

  toggleQuantityVisibility(slug: string) {
    const state = this.initQuantityState(slug);
    if (!this.isLogin) return;
    state.visible = true;
  }
  navigateToBrand(slug: string) {
    this.router.navigateByUrl(`brand/${slug}`);
  }
  async getFeatured() {
    try {
      let featured = await this.api.getFeaturedProducts();
      this.featuredProducts = featured.map((item: any, index: number) => ({
        ...item, _idx: index,
      }));
      this.updateCanScrollFeatured();
    } catch (err) {
      console.log(err);
    }
    // this.featuredProducts=     [
    //     {
    //         "urlSlug": "avery-uc900-722-t-pantone-322c-medium-teal-translucent-vinyl-48-x-50yd",
    //         "productName": "Avery UC900-722-T Pantone 322C Medium Teal Translucent Vinyl 48\" x 50Yd",
    //         "productDescription": null,
    //         "imageUrl": "https://sfsuppleis.blob.core.windows.net/images/products/VA9622-T-U.jpg",
    //         "isSingle": false,
    //         "lastViewedOn": null,
    //         "price": null,
    //         "isWishlisted": false,
    //         "attributesDTOs": [
    //             {
    //                 "attributeName": "Adhesive_Color",
    //                 "attributeValue": "Clear",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Adhesive_Type",
    //                 "attributeValue": "Permenant",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "App_Type",
    //                 "attributeValue": "Backlit Signage",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Color",
    //                 "attributeValue": "Parakeet Green",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Finish",
    //                 "attributeValue": "Matte",
    //                 "isFiltered": true
    //             },
    //             {
    //                 "attributeName": "Length",
    //                 "attributeValue": "50 yrds",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Material",
    //                 "attributeValue": "Ultimate Cast",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Model",
    //                 "attributeValue": "UC-900-722-T",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Rating",
    //                 "attributeValue": "7 Years",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Size",
    //                 "attributeValue": "48\" x 50 Yards",
    //                 "isFiltered": true
    //             },
    //             {
    //                 "attributeName": "Thickness",
    //                 "attributeValue": "2.5 Mil",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Type",
    //                 "attributeValue": "Translucent",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Width",
    //                 "attributeValue": "48\"",
    //                 "isFiltered": true
    //             }
    //         ],
    //         "reviews": [],
    //         "isInCart": false,
    //         "cartQty": 0,
    //         "attributeSelector": null,
    //         "rating": 0.0,
    //         "isStockItem": false
    //     },
    //     {
    //         "urlSlug": "exotic-backlit-vinyl-awning-fabric-1508-medium-green-61-15oz",
    //         "productName": "Exotic Backlit Vinyl Awning Fabric 1508 Medium Green 61\" 15oz.",
    //         "productDescription": null,
    //         "imageUrl": "https://sfsuppleis.blob.core.windows.net/images/products/AWF1508.jpg",
    //         "isSingle": false,
    //         "lastViewedOn": null,
    //         "price": null,
    //         "isWishlisted": false,
    //         "attributesDTOs": [
    //             {
    //                 "attributeName": "Color",
    //                 "attributeValue": "Medium Green",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Material",
    //                 "attributeValue": "Fabric",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Model",
    //                 "attributeValue": "1508",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Type",
    //                 "attributeValue": "Backlit Vinyl",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Weight",
    //                 "attributeValue": "15oz.",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Width",
    //                 "attributeValue": "61\"",
    //                 "isFiltered": true
    //             }
    //         ],
    //         "reviews": [],
    //         "isInCart": false,
    //         "cartQty": 0,
    //         "attributeSelector": null,
    //         "rating": 0.0,
    //         "isStockItem": false
    //     },
    //     {
    //         "urlSlug": "ronan-b162-purple-bulletin-paint-quart",
    //         "productName": "Ronan #B162 Purple Bulletin Paint Quart",
    //         "productDescription": null,
    //         "imageUrl": "https://sfsuppleis.blob.core.windows.net/images/products/PRB162Q.jpg",
    //         "isSingle": false,
    //         "lastViewedOn": null,
    //         "price": null,
    //         "isWishlisted": false,
    //         "attributesDTOs": [
    //             {
    //                 "attributeName": "Finish",
    //                 "attributeValue": "High Gloss",
    //                 "isFiltered": true
    //             },
    //             {
    //                 "attributeName": "Material",
    //                 "attributeValue": "Paint to be broken into further details",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Model",
    //                 "attributeValue": "B162",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Size",
    //                 "attributeValue": "Quart",
    //                 "isFiltered": true
    //             },
    //             {
    //                 "attributeName": "Type",
    //                 "attributeValue": "Bulletin",
    //                 "isFiltered": false
    //             }
    //         ],
    //         "reviews": [
    //             {
    //                 "comment": "You must try this product ",
    //                 "customerName": "Tech Craft Innovations",
    //                 "email": "yidelbraver@gmail.com",
    //                 "rating": 5
    //             },
    //             {
    //                 "comment": "product review test",
    //                 "customerName": "Techcraft ",
    //                 "email": "shaul@techcraftinnovations.com",
    //                 "rating": 5
    //             },
    //             {
    //                 "comment": ";oj",
    //                 "customerName": "Techcraft ",
    //                 "email": "shaul@techcraftinnovations.com",
    //                 "rating": 5
    //             },
    //             {
    //                 "comment": "\n\"o[",
    //                 "customerName": "Techcraft ",
    //                 "email": "shaul@techcraftinnovations.com",
    //                 "rating": 5
    //             },
    //             {
    //                 "comment": "Product was good",
    //                 "customerName": "Tech Craft Innovations",
    //                 "email": "yidelbraver@gmail.com",
    //                 "rating": 10
    //             },
    //             {
    //                 "comment": "Product was good",
    //                 "customerName": "Tech Craft Innovations",
    //                 "email": "yidelbraver@gmail.com",
    //                 "rating": 10
    //             },
    //             {
    //                 "comment": "lkug",
    //                 "customerName": "Techcraft ",
    //                 "email": "shaul@techcraftinnovations.com",
    //                 "rating": 5
    //             },
    //             {
    //                 "comment": "very good",
    //                 "customerName": "Techcraft ",
    //                 "email": "shaul@techcraftinnovations.com",
    //                 "rating": 10
    //             }
    //         ],
    //         "isInCart": false,
    //         "cartQty": 0,
    //         "attributeSelector": null,
    //         "rating": 6.88,
    //         "isStockItem": false
    //     },
    //     {
    //         "urlSlug": "tap-bolts-38-x-6",
    //         "productName": "Tap Bolts 3/8\" x 6\"",
    //         "productDescription": null,
    //         "imageUrl": "https://sfsuppleis.blob.core.windows.net/images/products/HDTB38600.jpg",
    //         "isSingle": false,
    //         "lastViewedOn": null,
    //         "price": null,
    //         "isWishlisted": false,
    //         "attributesDTOs": [
    //             {
    //                 "attributeName": "Length",
    //                 "attributeValue": "6\"",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Width",
    //                 "attributeValue": "3/8\"",
    //                 "isFiltered": true
    //             }
    //         ],
    //         "reviews": [
    //             {
    //                 "comment": "product testing",
    //                 "customerName": "Tech Craft Innovations",
    //                 "email": "yidelbraver@gmail.com",
    //                 "rating": 3
    //             }
    //         ],
    //         "isInCart": false,
    //         "cartQty": 0,
    //         "attributeSelector": null,
    //         "rating": 3.0,
    //         "isStockItem": false
    //     },
    //       {
    //         "urlSlug": "tap-bolts-38-x-6",
    //         "productName": "Tap Bolts 3/8\" x 6\"",
    //         "productDescription": null,
    //         "imageUrl": "https://sfsuppleis.blob.core.windows.net/images/products/HDTB38600.jpg",
    //         "isSingle": false,
    //         "lastViewedOn": null,
    //         "price": null,
    //         "isWishlisted": false,
    //         "attributesDTOs": [
    //             {
    //                 "attributeName": "Length",
    //                 "attributeValue": "6\"",
    //                 "isFiltered": false
    //             },
    //             {
    //                 "attributeName": "Width",
    //                 "attributeValue": "3/8\"",
    //                 "isFiltered": true
    //             }
    //         ],
    //         "reviews": [
    //             {
    //                 "comment": "product testing",
    //                 "customerName": "Tech Craft Innovations",
    //                 "email": "yidelbraver@gmail.com",
    //                 "rating": 3
    //             }
    //         ],
    //         "isInCart": false,
    //         "cartQty": 0,
    //         "attributeSelector": null,
    //         "rating": 3.0,
    //         "isStockItem": false
    //     }
    // ]
  }
  // async handlePlus(cart: any, event: Event) {
  //   console.log(cart, 'cart from categoreis ')
  //   let token = localStorage.getItem('token');

  //   if (!token) {
  //     this.isLogin = false;
  //     this.header.clearUserDetail();
  //     return;
  //   } else {
  //     if (
  //       (cart.productStatus == 4 ||
  //         cart.productStatus == 0 ||
  //         !cart.productStatus) &&
  //       token
  //     ) {
  //       this.popupShipFees.statusFour = true;
  //       return;
  //     } else if (cart.productStatus == 3 && token) {
  //       this.feesCart = cart;
  //       this.popupShipFees.isVisible = true;
  //       return;
  //     } else {
  //       event.stopPropagation();
  //        let res = await this.performAddToCart(cart, 1);
  //           let item = res.items.filter((item:any)=>item.product.urlSlug == cart.urlSlug )
  //           cart.cartQty = item[0].qty;
  //           cart.qty = item[0].qty;
  //     }
  //   }
  // }


  navigateToProduct(slug: string) {
    this.router.navigateByUrl(`product/${slug}`);
  }
  out(slug: string) {
    console.log('out called');
    const state = this.showQuantityState[slug];
    if (!state) return;

    if (!state.locked) {
      state.visible = false;
    }
  }
  async onClickCompare(item: any) {
    if (isPlatformBrowser(this.platformId)) {
      const compareList = JSON.parse(localStorage.getItem('compareList') || '[]');
      console.log(compareList.length, 'compareList length');
      const existingItem = compareList.find(
        (i: any) => i.productName === item.productName
      );
      if (!existingItem) {
        compareList.push(item);
        localStorage.setItem('compareList', JSON.stringify(compareList));
        console.log('Item added to compare list:', item);
        this.util.showQuotesToast('Success', 'success', 'Item Added to compare List');
      } else {
        console.log('Item already exists in compare list');
        this.util.showQuotesToast('Error', 'error', 'Item Already exists in compare list');
      }
      console.log(item, 'item for compare');
    }
  }

  isItemInCompareList(item: any): boolean {
    if (isPlatformBrowser(this.platformId)) {
      const compareList = JSON.parse(localStorage.getItem('compareList') || '[]');
      return compareList.some((i: any) => i.productName === item.productName);
    }
    return false
  }
  goToAll() {
    this.router.navigateByUrl('category/all');
  }


  //  closeAddtocart() {
  //   this.addedToCart = false;
  //   this.addedItem = null;
  //   this.util.setBackdrop(false);
  //   this.showEnabled = false;
  // }
  // openAddtocart(product: any) {
  //   if (!this.showEnabled) return;
  //   this.addedItem = product;
  //   this.addedToCart = true;
  //   this.util.setBackdrop(true);
  //   this.showEnabled = false;
  // }
}
